import { useEffect, useRef, useState } from "react";
import { FileUp, Loader2, Receipt, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { InvoiceReview } from "./InvoiceReview";

export const VendorInvoicesTab = ({ onApplied }) => {
  const [list, setList] = useState(null);
  const [ings, setIngs] = useState([]);
  const [open, setOpen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [v, i] = await Promise.all([api.get("/admin/kitchen/vendor-invoices"), api.get("/admin/kitchen/ingredients")]);
    setList(v.data.invoices);
    setIngs(i.data.ingredients);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/kitchen/vendor-invoices", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setOpen(data.invoice);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const openInvoice = async (id) => setOpen((await api.get(`/admin/kitchen/vendor-invoices/${id}`)).data.invoice);

  if (!list) return <Loader2 className="h-5 w-5 animate-spin text-brand-teal" />;
  if (open) return <InvoiceReview key={open.id} invoice={open} ings={ings} onBack={() => setOpen(null)} onApplied={() => { setOpen(null); load(); onApplied(); }} />;
  return (
    <div className="space-y-3" data-testid="vendor-invoices-tab">
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="vinv-file-input" />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="vinv-upload-btn"
        className="w-full rounded-2xl border-2 border-dashed border-brand-teal/50 bg-white p-6 text-center hover:bg-brand-teal/5 transition-colors disabled:opacity-60">
        {uploading ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-teal" /> : <FileUp className="mx-auto h-6 w-6 text-brand-teal" />}
        <p className="mt-2 font-bold text-[14px] text-brand-charcoal">{uploading ? "Reading your invoice… (about 15–30 seconds)" : "Upload a vendor invoice"}</p>
        <p className="text-[12px] text-brand-charcoal/55">PDF or photo. AI reads the items and prices, then you review before anything changes.</p>
      </button>
      {list.length === 0 ? (
        <p className="text-[13px] text-brand-charcoal/55" data-testid="vinv-empty">No invoices uploaded yet.</p>
      ) : list.map((v) => (
        <button key={v.id} type="button" onClick={() => openInvoice(v.id)} data-testid={`vinv-row-${v.id}`} className="w-full flex items-center gap-3 rounded-xl bg-white border border-black/10 px-4 py-2.5 text-left">
          <Receipt className="h-4 w-4 text-brand-charcoal/40" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[14px] text-brand-charcoal truncate">{v.vendor || v.filename}</p>
            <p className="text-[11.5px] text-brand-charcoal/55">#{v.invoice_number || "—"} · {v.invoice_date || v.created_at.slice(0, 10)}</p>
          </div>
          <span className={`text-[10.5px] font-bold uppercase tracking-[0.1em] ${v.status === "applied" ? "text-brand-teal" : "text-brand-gold-dark"}`}>{v.status === "applied" ? "Applied" : "Needs review"}</span>
          <ChevronRight className="h-4 w-4 text-brand-charcoal/40" />
        </button>
      ))}
    </div>
  );
};
